import { useQueryParams } from "@/hooks/use-query-param";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useCreatePatientSocialHistory } from "@/queries/history/social-history/use-create-patient-social-history";
import { useUpdatePatientSocialHistory } from "@/queries/history/social-history/use-update-patient-social-history";
import {
  PatientQuestionAnswer,
  PatientQuestionAnswerRequest,
  PatientSocialHistoryControllerApi,
  QuestionAnswerTypeEnum,
  SocialHistoryQuestionsControllerApi,
} from "@/services";
import {
  getConfigObject,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  SOCIAL_HISTORY_LIST_KEY,
  SOCIAL_HISTORY_TYPE,
} from "../readmission-f2f-constant";
import {
  ChooseOneValue,
  GroupValue,
  SocialHistoryQuestion,
} from "../readmission-f2f-types";
import { buildSocialAnswers, SocialStore } from "../readmission-f2f-utils";
import { SOCIAL_HISTORY_GROUPS } from "../readmission-f2f-social-constant";

interface RawQuestion {
  uuid?: string;
  question?: string;
  answerType?: string;
  sequenceId?: number;
  answersOptions?: { answer?: string }[];
}
interface SavedAnswerRow {
  uuid?: string;
  answer?: string;
  notes?: string;
  question?: { uuid?: string };
}

export interface UseSocialHistorySection {
  questions: SocialHistoryQuestion[];
  store: SocialStore;
  isLoading: boolean;
  isSaving: boolean;
  isDirty: boolean;
  setValue: (uuid: string, value: GroupValue | ChooseOneValue) => void;
  save: () => Promise<void>;
}

const splitAnswer = (answer: string): string[] =>
  answer
    ? answer
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
    : [];

export const useSocialHistorySection = (): UseSocialHistorySection => {
  const { patientId } = useParams() as { patientId?: string };
  const appointmentId = useQueryParams("appointmentid");
  const xTENANTID = getXtenantId();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const { mutateAsync: createSocial, isPending: isCreating } =
    useCreatePatientSocialHistory();
  const { mutateAsync: updateSocial, isPending: isUpdating } =
    useUpdatePatientSocialHistory();

  const { data: questions = [], isLoading: isLoadingQuestions } = useQuery({
    queryKey: ["social-history-questions", xTENANTID],
    refetchOnWindowFocus: false,
    queryFn: () =>
      new SocialHistoryQuestionsControllerApi(getConfigObject())
        .getAllSocialHistoryQuestionsForPatient(
          SOCIAL_HISTORY_TYPE,
          0,
          100,
          undefined,
          undefined,
          xTENANTID
        )
        .then((res) => {
          const items = (res?.data?.data ?? []) as { question?: RawQuestion }[];
          return items
            .map((item) => item.question)
            .filter((q): q is RawQuestion => !!q?.uuid && !!q?.question)
            .map<SocialHistoryQuestion>((q) => {
              // The API only types answers as STRING/TEXT/BOOLEAN; single-choice groups come from the template.
              const group = SOCIAL_HISTORY_GROUPS.find(
                (g) => g.title === q.question
              );
              return {
                uuid: q.uuid as string,
                title: q.question as string,
                answerType: q.answerType ?? QuestionAnswerTypeEnum.String,
                options: (q.answersOptions ?? [])
                  .map((option) => option.answer ?? "")
                  .filter(Boolean),
                sequenceId: q.sequenceId ?? 0,
                chooseOne: group?.chooseOne,
                withFreeText: group?.withFreeText,
              };
            })
            .sort((a, b) => a.sequenceId - b.sequenceId);
        }),
  });

  const { data: savedRows = [], isLoading: isLoadingSaved } = useQuery({
    queryKey: [SOCIAL_HISTORY_LIST_KEY, patientId, appointmentId, xTENANTID],
    refetchOnWindowFocus: false,
    enabled: !!patientId,
    queryFn: () =>
      new PatientSocialHistoryControllerApi(getConfigObject())
        .getPatientSocialHistory(
          SOCIAL_HISTORY_TYPE,
          patientId,
          true,
          false,
          appointmentId ?? undefined,
          xTENANTID
        )
        .then((res) => {
          const data = (res?.data?.data ?? {}) as {
            patientQuestionAnswers?: SavedAnswerRow[];
          };
          return data.patientQuestionAnswers ?? [];
        }),
  });

  const savedByUuid = useMemo(() => {
    const map = new Map<
      string,
      { uuid?: string; answer: string; notes: string }
    >();
    savedRows.forEach((row) => {
      const questionUuid = row?.question?.uuid;
      if (questionUuid)
        map.set(questionUuid, {
          uuid: row.uuid,
          answer: row.answer ?? "",
          notes: row.notes ?? "",
        });
    });
    return map;
  }, [savedRows]);

  const initialStore = useMemo<SocialStore>(() => {
    const store: SocialStore = {};
    questions.forEach((q) => {
      const saved = savedByUuid.get(q.uuid);
      if (q.answerType === QuestionAnswerTypeEnum.Text) {
        store[q.uuid] = { selected: "", freeText: saved?.answer ?? "" };
      } else if (
        q.answerType === QuestionAnswerTypeEnum.Boolean ||
        q.chooseOne
      ) {
        store[q.uuid] = {
          selected: saved?.answer ?? "",
          freeText: saved?.notes ?? "",
        };
      } else {
        const known = splitAnswer(saved?.answer ?? "").filter((label) =>
          q.options.includes(label)
        );
        store[q.uuid] = {
          selected: known,
          details: {},
          freeText: saved?.notes ?? "",
        };
      }
    });
    return store;
  }, [questions, savedByUuid]);

  const [store, setStore] = useState<SocialStore>({});
  const signature = useMemo(() => JSON.stringify(initialStore), [initialStore]);
  const lastSignature = useRef<string | null>(null);
  useEffect(() => {
    if (lastSignature.current !== signature) {
      lastSignature.current = signature;
      setStore(initialStore);
    }
  }, [signature, initialStore]);

  const persist = useCallback(
    async (next: SocialStore) => {
      const savedUuids = new Map<string, string | undefined>();
      savedByUuid.forEach((row, questionUuid) =>
        savedUuids.set(questionUuid, row.uuid)
      );
      const answers = buildSocialAnswers(
        questions,
        next,
        savedUuids,
        patientId
      );
      if (!answers.length) return;

      const base = {
        patient: { [patientId ?? ""]: patientId ?? "" },
        appointmentId: appointmentId ?? undefined,
        socialHistoryType: SOCIAL_HISTORY_TYPE,
      };
      const send = (
        rows: PatientQuestionAnswer[],
        mutate: (vars: {
          patientQuestionAnswerRequest: PatientQuestionAnswerRequest;
          xTENANTID?: string;
        }) => Promise<unknown>
      ) =>
        rows.length
          ? mutate({
              patientQuestionAnswerRequest: {
                ...base,
                patientQuestionAnswers: rows,
              },
              xTENANTID,
            })
          : null;

      const calls = [
        send(
          answers.filter((row) => !row.uuid),
          createSocial
        ),
        send(
          answers.filter((row) => row.uuid),
          updateSocial
        ),
      ].filter((call): call is Promise<unknown> => call !== null);
      if (!calls.length) return;
      try {
        const results = await Promise.all(calls);
        showSnackbar(getSuccessSnackbar(results.at(-1)));
        await queryClient.invalidateQueries({
          queryKey: [SOCIAL_HISTORY_LIST_KEY],
        });
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [
      questions,
      savedByUuid,
      patientId,
      appointmentId,
      xTENANTID,
      createSocial,
      updateSocial,
      showSnackbar,
      queryClient,
    ]
  );

  const save = useCallback(() => persist(store), [persist, store]);

  const setValue = useCallback(
    (uuid: string, value: GroupValue | ChooseOneValue) => {
      setStore((prev) => ({ ...prev, [uuid]: value }));
    },
    []
  );

  return {
    questions,
    store,
    isLoading: isLoadingQuestions || isLoadingSaved,
    isSaving: isCreating || isUpdating,
    isDirty: JSON.stringify(store) !== signature,
    setValue,
    save,
  };
};
